import { ReactElement, useEffect, useId, useRef } from 'react';
import { ComponentRendering } from '@sitecore-jss/sitecore-jss/layout';
import { Field } from '@sitecore-jss/sitecore-jss-nextjs';

import { useCondensedMenu } from 'common/condensedMenu/CondensedMenuProvider';
import { mapSharedPropertiesToFields } from 'common/getters';
import { SitecoreFieldTypes } from 'common/types/sitecore';

export type CondensedMenuItem = {
  condensedMenuAnchor?: Field<string>;
  condensedMenuEnabled?: Field<boolean>;
  condensedMenuTitle?: Field<string>;
};

export interface CondensedMenuWrapperProps {
  children: ReactElement;
  rendering: ComponentRendering<SitecoreFieldTypes & { data?: { item: CondensedMenuItem } }>;
}

export const CondensedMenuWrapper = ({ children, rendering }: CondensedMenuWrapperProps) => {
  const id = useId();

  const parsedProps = mapSharedPropertiesToFields<CondensedMenuItem>(
    rendering.fields as SitecoreFieldTypes
  );

  const paddingProps = rendering.fields?.data ? rendering.fields.data.item : parsedProps;
  const name = paddingProps?.condensedMenuTitle?.value ?? rendering.componentName;
  const anchor = paddingProps?.condensedMenuAnchor?.value ?? rendering.uid ?? id;
  const enabled = !!paddingProps?.condensedMenuEnabled?.value;
  const ref = useCondensedMenuAnchor(name, anchor, enabled);

  if (!enabled) {
    return <>{children}</>;
  }

  return (
    <div ref={ref} id={anchor}>
      {children}
    </div>
  );
};

export const useCondensedMenuAnchor = (
  name: string | undefined,
  anchor: string | undefined,
  enabled: boolean
) => {
  const { registerItem, unregisterItem } = useCondensedMenu();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (enabled && anchor) {
      registerItem({ name, anchor, anchorElement: ref });
    }
    return () => {
      if (anchor) {
        unregisterItem(anchor);
      }
    };
  }, [registerItem, unregisterItem, name, anchor, enabled]);
  return ref;
};
